import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import moment from "moment";
import PropTypes from "prop-types";
import { Card, Grid } from "@mui/material";

import { statusChartColors } from "common/constant";
import { getOptionValue, halfYearEnum } from "common/enums";
import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import InspectionStatusPieChart from "Components/Charts/InspectionStatusPieChart";
import InspectionStatusBarChart from "Components/Charts/InspectionStatusBarChart";
import CommonInspectionsBarChart from "Components/Charts/CommonInspectionsBarChart";
import ComplianceRateOverTimeLineChart from "Components/Charts/ComplianceRateOverTimeLineChart";
import ComplianceLevelsByBuildingBarChart from "Components/Charts/ComplianceLevelsByBuildingBarChart";
import OverallComplianceBarChart from "Components/Charts/OverallComplianceBarChart";
import DatePicker from "Components/Common/DatePicker";
import SelectField from "Components/Common/SelectField";
import NonComplianceCorrectiveActionBarChart from "Components/Charts/NonComplianceCorrectiveActionBarChart";
import {
  staticCommonInspectionBarChart,
  staticComplianceRateOverTime,
  staticDeveloperBuildingOptions,
  staticInspectionObservationBuildings,
  staticInspectionStatusMonthWise,
  staticNonComplianceBuildingData,
} from "./firstHalfDashboardData";

import getChartData from "utils/getChartData";

import {
  useLazyGetCommonInspectionObservationQuery,
  useLazyGetComplianceRateOverTimeQuery,
  useLazyGetDeveloperBuildingComplianceQuery,
  useLazyGetInspectionObservationBuildingComplianceQuery,
  useLazyGetInspectionStatusQuery,
  useLazyGetNonComplianceCorrectiveActionQuery,
} from "apis/FireSafety/DashBoard";

const FireSafetyDashboard = ({ t }) => {
  const [
    getInspectionStatus,
    {
      data: {
        data: {
          overallComplianceMonthWise: inspectionStatusMonthWiseData = [],
        } = {},
      } = {},
      isLoading: isInspectionStatusLoading,
      isFetching: isInspectionStatusFetching,
    },
  ] = useLazyGetInspectionStatusQuery();

  const [
    getCommonInspections,
    {
      data: { data: commonInspectionsBarChartData = [] } = {},
      isLoading: isCommonInspectionBarChartDataLoading,
      isFetching: isCommonInspectionBarChartDataFetching,
    },
  ] = useLazyGetCommonInspectionObservationQuery();

  const [
    getComplianceRateOverTime,
    { data: { data: { complianceRateOverTimeHalfYearWise = [] } = {} } = {} },
  ] = useLazyGetComplianceRateOverTimeQuery();

  const [
    getDeveloperBuildingCompliance,
    {
      data: { data: developerBuildingCompliance = [] } = {},
      isLoading: isDeveloperBuildingComplianceLoading,
      isFetching: isDeveloperBuildingComplianceFetching,
    },
  ] = useLazyGetDeveloperBuildingComplianceQuery();

  const [
    getInspectionObservationBuildingCompliance,
    { data: { data: inspectionObservationBuildings = [] } = {} },
  ] = useLazyGetInspectionObservationBuildingComplianceQuery();

  const [
    getNonComplianceCorrectiveAction,
    { data: { data: nonComplianceBuildingData = [] } = {} },
  ] = useLazyGetNonComplianceCorrectiveActionQuery();

  const [inspectionStatusCategory, setInspectionStatusCategory] = useState({
    title: "",
    color: statusChartColors[0],
  });

  const [query, setQuery] = useState({
    year: moment(),
    halfYear: getOptionValue("halfYearEnum", "First Half"),
    developerBuildingId: null,
  });

  const isFirstHalfAndYear2024Selected = useMemo(
    () =>
      getOptionValue("halfYearEnum", "First Half") === query.halfYear &&
      +query.year.format("YYYY") === 2024,
    [query]
  );

  const {
    nonComplianceBuildingsData,
    complianceRateOverTimeHalfYearly,
    inspectionObservationBuildingsData,
    commonInspectionsBarChartsData,
    overallComplianceMonthWise,
    inspectionStatusMonthWise,
  } = useMemo(
    () => ({
      nonComplianceBuildingsData: isFirstHalfAndYear2024Selected
        ? staticNonComplianceBuildingData
        : nonComplianceBuildingData,
      complianceRateOverTimeHalfYearly: isFirstHalfAndYear2024Selected
        ? staticComplianceRateOverTime[query?.developerBuildingId ?? 1]
        : complianceRateOverTimeHalfYearWise,
      inspectionObservationBuildingsData: isFirstHalfAndYear2024Selected
        ? staticInspectionObservationBuildings
        : inspectionObservationBuildings,
      commonInspectionsBarChartsData: isFirstHalfAndYear2024Selected
        ? staticCommonInspectionBarChart
        : commonInspectionsBarChartData,

      inspectionStatusMonthWise: isFirstHalfAndYear2024Selected
        ? staticInspectionStatusMonthWise
        : inspectionStatusMonthWiseData,
    }),
    [
      isFirstHalfAndYear2024Selected,
      query?.developerBuildingId,

      inspectionStatusMonthWiseData,
      commonInspectionsBarChartData,
      nonComplianceBuildingData,
      complianceRateOverTimeHalfYearWise,
      inspectionObservationBuildings,
    ]
  );

  const {
    pieLabels: inspectionStatusPieChartLabels,
    pieChartData: inspectionStatusPieChartData,
  } = getChartData(
    inspectionStatusMonthWise,
    "overallComplianceCityCount",
    "category",
    "count",
    "month"
  );

  const buildingOptions = useMemo(
    () =>
      isFirstHalfAndYear2024Selected
        ? staticDeveloperBuildingOptions
        : developerBuildingCompliance.map((building) => ({
            label: building.name,
            value: building.id,
          })),
    [developerBuildingCompliance, isFirstHalfAndYear2024Selected]
  );

  useEffect(() => {
    setQuery({
      ...query,
      ...(buildingOptions.length && {
        developerBuildingId: buildingOptions[0].value,
      }),
    });
  }, [buildingOptions]);

  useEffect(() => {
    try {
      const payload = {
        year: query.year.format("YYYY"),
        halfYear: query.halfYear,
      };

      if (!isFirstHalfAndYear2024Selected) {
        (() => {
          getDeveloperBuildingCompliance(payload);

          getNonComplianceCorrectiveAction(payload);

          getInspectionStatus(payload);

          getCommonInspections(payload);

          getComplianceRateOverTime({
            year: query.year.format("YYYY"),
            ...(query.developerBuildingId && {
              developerBuildingId: query.developerBuildingId,
            }),
          });

          getInspectionObservationBuildingCompliance({
            ...payload,
            ...(query.developerBuildingId && {
              developerBuildingId: query.developerBuildingId,
            }),
          });
        })();
      }
    } catch (error) {
      console.error(error);
    }
  }, [query, isFirstHalfAndYear2024Selected]);

  useEffect(() => {
    if (inspectionStatusMonthWise.length) {
      setInspectionStatusCategory((prev) => ({
        ...prev,
        title:
          inspectionStatusMonthWise[0].overallComplianceCityCount[0].category,
      }));
    }
  }, [overallComplianceMonthWise, inspectionStatusMonthWise]);

  return (
    <div className="page-content">
      <BreadCrumb title={t("Dashboard")} />

      <Backdrop
        open={
          isInspectionStatusLoading ||
          isCommonInspectionBarChartDataLoading ||
          isDeveloperBuildingComplianceLoading ||
          isInspectionStatusFetching ||
          isCommonInspectionBarChartDataFetching ||
          isDeveloperBuildingComplianceFetching
        }
      />

      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid item xs={6} sm={5} md={4} lg={3}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                label={t("Select Year")}
                className="w-100"
                views={["year"]}
                value={query.year}
                disableFuture
                onChange={(date) => {
                  setQuery({
                    ...query,
                    year: date,
                  });
                }}
              />
            </Grid>
            <Grid item xs={6} sm={5} md={4} lg={3}>
              <SelectField
                label={t("Select half")}
                options={halfYearEnum}
                value={
                  halfYearEnum.find(({ value }) => value === query.halfYear) ??
                  ""
                }
                onChange={(_, { value }) =>
                  setQuery({
                    ...query,
                    halfYear: value,
                    developerBuildingId: buildingOptions[0]?.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Card className="overflow-auto d-flex flex-column">
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <InspectionStatusPieChart
                  changeCategoryType={setInspectionStatusCategory}
                  data={inspectionStatusPieChartData}
                  labels={inspectionStatusPieChartLabels}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InspectionStatusBarChart
                  categoryType={inspectionStatusCategory}
                  inspectionStatusMonthWise={inspectionStatusMonthWise}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ height: "auto" }}>
            <OverallComplianceBarChart
              nonComplianceBuildingData={nonComplianceBuildingsData}
            />
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <NonComplianceCorrectiveActionBarChart
              nonComplianceBuildingData={nonComplianceBuildingsData}
            />
          </Card>
        </Grid>

        <Grid item xs={8} sm={5} md={4} lg={3}>
          <SelectField
            label="Select building"
            options={buildingOptions}
            value={
              buildingOptions.find(
                ({ value }) => value === query.developerBuildingId
              ) ?? ""
            }
            onChange={(_, { value }) =>
              setQuery({ ...query, developerBuildingId: value })
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Card>
                <ComplianceRateOverTimeLineChart
                  lineChartData={complianceRateOverTimeHalfYearly}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <ComplianceLevelsByBuildingBarChart
                  chartData={
                    isFirstHalfAndYear2024Selected
                      ? inspectionObservationBuildingsData[
                          query?.developerBuildingId ?? 1
                        ]
                      : inspectionObservationBuildingsData
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CommonInspectionsBarChart
              chartData={commonInspectionsBarChartsData}
            />
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

FireSafetyDashboard.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyDashboard);
